<template>
    <ManagePageContainer>
        <ManageSidebar />
        <MainContentWrapper>
            <!-- * 헤더 섹션 -->
            <HeaderSection>
                <HeaderTitle>
                    <h1>거래처 관리</h1>
                    <span>등록된 거래처 정보를 관리합니다</span>
                </HeaderTitle>
                <HeaderActions>
                    <PrimaryButton @click="openBulkRegistrationModal">
                        <ListBoxOutline :size="24" :style="{ paddingTop: '3px' }" />거래처 일괄등록
                    </PrimaryButton>
                    <PrimaryButton @click="openRegistrationModal">
                        <Plus :size="24" :style="{ paddingTop: '3px' }" />거래처 등록
                    </PrimaryButton>
                </HeaderActions>
            </HeaderSection>

            <!-- * 통계 카드 -->
            <StatsSection>
                <StatsCard>
                    <StatsNumber>{{ totalCompanies }}</StatsNumber>
                    <StatsLabel>현재 등록된 거래처</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber>{{ favoriteCompanies }}</StatsNumber>
                    <StatsLabel>즐겨찾기</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber>{{ recentUpdates }}</StatsNumber>
                    <StatsLabel>최근 갱신</StatsLabel>
                </StatsCard>
            </StatsSection>

            <!-- * 검색 및 필터 섹션 -->
            <SearchAndUpdateSection>
                <!-- * 검색 카드 -->
                <SearchCard>
                    <SearchRow>
                        <SearchGroup>
                            <label>검색어</label>
                            <SearchInput type="text" v-model="searchQuery" placeholder="거래처명을 입력하세요"
                                @keyup.enter="searchHandler" />
                            <SearchButton @click="searchHandler">
                                <Magnify :size="20" />
                            </SearchButton>
                        </SearchGroup>
                    </SearchRow>

                    <!-- * 통합된 검색 필터 -->
                    <SearchRow>
                        <FilterGroup>
                            <label>
                                구분
                            </label>
                            <FilterChips>
                                <ManageButton v-if="checkAdminPermission()" @click="openCompanyTypeModal">
                                    <Cog :size="18" :style="{ paddingTop: '3px' }" /> 관리
                                </ManageButton>
                                <FilterChip v-for="typeCode in sortedCompanyTypeCodes" :key="typeCode"
                                    :active="searchType === typeCode" @click="filterByType(typeCode)">
                                    {{ companyTypes[typeCode] }}
                                </FilterChip>
                            </FilterChips>
                        </FilterGroup>
                    </SearchRow>

                    <SearchRow>
                        <FilterGroup>
                            <label>지역</label>
                            <FilterChips>
                                <FilterChip v-for="(areaName, areaCode) in companyAreas" :key="areaCode"
                                    :active="searchArea === areaCode" @click="filterByArea(areaCode)">
                                    {{ areaName }}
                                </FilterChip>
                            </FilterChips>
                        </FilterGroup>
                    </SearchRow>

                    <!-- * 활성 필터 표시 -->
                    <SearchRow v-if="hasActiveFilters">
                        <FilterGroup>
                            <label>활성 필터</label>
                            <ActiveFilterChips>
                                <ActiveFilterChip v-if="searchType !== '00'" @click="clearFilter('type')">
                                    {{ companyTypes[searchType] }} ✕
                                </ActiveFilterChip>
                                <ActiveFilterChip v-if="searchArea !== '000'" @click="clearFilter('area')">
                                    {{ companyAreas[searchArea] }} ✕
                                </ActiveFilterChip>
                                <ActiveFilterChip v-if="favoriteFilter === 'favorites'"
                                    @click="clearFilter('favorite')">
                                    즐겨찾기만 ✕
                                </ActiveFilterChip>
                                <ClearAllFiltersButton @click="clearAllFilters">
                                    모든 필터 초기화
                                </ClearAllFiltersButton>
                            </ActiveFilterChips>
                        </FilterGroup>
                    </SearchRow>
                </SearchCard>

                <!-- * 갱신 정보 카드 -->
                <UpdateInfoCard>
                    <UpdateInfoTitle>거래처 갱신 정보</UpdateInfoTitle>
                    <UpdateInfoContent>
                        <UpdateInfoRow>
                            <UpdateInfoItem>
                                <UpdateInfoLabel>갱신 타입</UpdateInfoLabel>
                                <UpdateTypeSelect v-model="selectedUpdateType">
                                    <option value="00">전체</option>
                                    <option value="13">기타</option>
                                    <option value="12">건설현장</option>
                                    <option value="11">보건소</option>
                                    <option value="10">병원</option>
                                    <option value="09">장기요양기관</option>
                                    <option value="08">복지용구</option>
                                    <option value="07">단기보호</option>
                                    <option value="06">주야간보호</option>
                                    <option value="05">방문간호</option>
                                    <option value="04">방문목욕</option>
                                    <option value="03">방문요양</option>
                                    <option value="02">노인요양공동생활가정</option>
                                    <option value="01">노인요양시설</option>
                                </UpdateTypeSelect>
                            </UpdateInfoItem>
                            <UpdateActionButton @click="updateCompany">
                                <Refresh :size="20" :style="{ paddingTop: '3px' }" />
                                갱신 실행
                            </UpdateActionButton>
                        </UpdateInfoRow>
                        <UpdateInfoItem>
                            <UpdateInfoLabel>마지막 갱신일</UpdateInfoLabel>
                            <UpdateInfoValue>{{ formattedLastUpdateDate }}</UpdateInfoValue>
                        </UpdateInfoItem>
                    </UpdateInfoContent>
                </UpdateInfoCard>
            </SearchAndUpdateSection>

            <!-- * 거래처 목록 테이블 -->
            <TableSection>
                <TableHeader>
                    <SubTitle>거래처 목록</SubTitle>
                    <ItemsPerPageSelector>
                        <select v-model="itemsPerPage" @change="changeItemsPerPage">
                            <option value="10">10개씩 보기</option>
                            <option value="20">20개씩 보기</option>
                            <option value="50">50개씩 보기</option>
                            <option value="100">100개씩 보기</option>
                        </select>
                    </ItemsPerPageSelector>
                </TableHeader>

                <!-- * 테이블 뷰 -->
                <TableContainer>
                    <ModernTable>
                        <thead>
                            <tr>
                                <th>즐겨찾기</th>
                                <th>거래처명</th>
                                <th>거래처번호</th>
                                <th>주소</th>
                                <th>전화번호</th>
                                <th>이메일</th>
                                <th>사업자 등록증</th>
                                <th>삭제</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="company in paginatedCompanies" :key="company.company_id"
                                :class="{ 'blurred': isTestUser }">
                                <td>
                                    <FavoriteButton @click="updateFavorite(company)">
                                        <FavoriteIcon :favorite="company.favorite">
                                            {{ company.favorite ? '⭐' : '☆' }}
                                        </FavoriteIcon>
                                    </FavoriteButton>
                                </td>
                                <td>
                                    <CompanyInfo>
                                        <CompanyName>{{ company.company_name }}</CompanyName>
                                    </CompanyInfo>
                                </td>
                                <td>
                                    <CompanyId>{{ company.company_id }}</CompanyId>
                                </td>
                                <td>
                                    <CompanyAddress>{{ company.address }}</CompanyAddress>
                                </td>
                                <td>
                                    <CompanyPhone>{{ company.tel }}</CompanyPhone>
                                </td>
                                <td>
                                    <CompanyEmail>{{ company.email }}</CompanyEmail>
                                </td>
                                <td>
                                    <CompanyRegistrationCertificate @click="viewBusinessLicense(company.company_id)"
                                        style="cursor: pointer; color: #0f4c5c; text-decoration: underline;">
                                        보기
                                    </CompanyRegistrationCertificate>
                                </td>
                                <td>
                                    <DeleteButton @click="deleteCompany(company.company_id)">
                                        <TrashCan :size="25" :style="{ paddingTop: '3px', color: '#dc2626' }" />
                                    </DeleteButton>
                                </td>
                            </tr>
                        </tbody>
                    </ModernTable>
                </TableContainer>

                <!-- * 페이지네이션 -->
                <PaginationContainer>
                    <Pagination>
                        <PaginationButton :disabled="currentPage === 1" @click="goToPage(1)">
                            <ChevronDoubleLeft :size="20" />
                        </PaginationButton>
                        <PaginationButton :disabled="currentPage <= 10" @click="goToPageGroup('prev')">
                            <ChevronLeft :size="20" />
                        </PaginationButton>

                        <PaginationNumber v-for="page in displayedPages" :key="page" :active="page === currentPage"
                            @click="goToPage(page)">
                            {{ page }}
                        </PaginationNumber>

                        <PaginationButton :disabled="currentPage > Math.floor(totalPages / 10) * 10"
                            @click="goToPageGroup('next')">
                            <ChevronRight :size="20" />
                        </PaginationButton>
                        <PaginationButton :disabled="currentPage === totalPages" @click="goToPage(totalPages)">
                            <ChevronDoubleRight :size="20" />
                        </PaginationButton>
                    </Pagination>
                </PaginationContainer>
            </TableSection>
        </MainContentWrapper>
        <!-- 구분 관리 모달 -->
        <CompanyTypeModal :show="showCompanyTypeModal" :companyTypes="customCompanyTypes"
            :companyKey="$globalState.company_key" @close="showCompanyTypeModal = false"
            @updated="handleCompanyTypeUpdated" />
    </ManagePageContainer>
</template>

<script>
import ManageSidebar from './ManageSidebar.vue';
import CompanyTypeModal from './modal/CompanyTypeModal.vue';
import * as ManageCompanyCss from '@/assets/styles/manage/ManageCompanyCss.js';

import ChevronDoubleLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import ChevronDoubleRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronLeft.vue';
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue';
import Magnify from 'vue-material-design-icons/Magnify.vue';
import Refresh from 'vue-material-design-icons/Refresh.vue';
import Plus from 'vue-material-design-icons/Plus.vue';
import ListBoxOutline from 'vue-material-design-icons/ListBoxOutline.vue';
import TrashCan from 'vue-material-design-icons/TrashCan.vue';
import Cog from 'vue-material-design-icons/Cog.vue';

export default {
    name: 'ManageCompany',

    components: {
        ManageSidebar,
        CompanyTypeModal,
        ...ManageCompanyCss,
        ChevronDoubleLeft,
        ChevronDoubleRight,
        ChevronLeft,
        ChevronRight,
        Magnify,
        Refresh,
        Plus,
        ListBoxOutline,
        TrashCan,
        Cog
    },

    data() {
        return {
            // 검색 관련
            searchQuery: '',
            searchSubmitted: '',
            searchType: '00',
            searchArea: '000',
            favoriteFilter: 'all',

            // 페이징
            currentPage: 1,
            totalPages: 1,
            itemsPerPage: 10,
            perPage: 100,

            // 갱신 관련
            selectedUpdateType: '00',
            lastUpdateDate: null,
            isLoading: false,

            // 거래처 데이터
            company_info: {
                companies: [],
                total_cnt: 0,
                favorite_cnt: 0
            },

            // 거래처 데이터
            companyTypes: {
                '00': '전체'
            },
            customCompanyTypes: [], // 사용자 정의 유형 목록

            // 지역 매핑
            companyAreas: {
                '000': '전체',
                '001': '서울',
                '002': '부산',
                '003': '대구',
                '004': '인천',
                '005': '광주',
                '006': '대전',
                '007': '울산',
                '008': '세종',
                '009': '경기',
                '010': '강원',
                '011': '충북',
                '012': '충남',
                '013': '전북',
                '014': '전남',
                '015': '경북',
                '016': '경남',
                '017': '제주'
            },

            // 모달 관련
            showRegistrationModal: false,
            showBulkRegistrationModal: false,
            showCompanyTypeModal: false
        }
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
        // 정렬된 거래처 타입 코드 배열 (전체가 첫 번째)
        sortedCompanyTypeCodes() {
            return ['00', ...this.customCompanyTypes.map(t => t.type_code)];
        },

        // 활성 필터가 있는지 확인
        hasActiveFilters() {
            return this.searchType !== '00' ||
                this.searchArea !== '000' ||
                this.favoriteFilter === 'favorites';
        },

        // 백엔드에서 이미 필터링된 데이터를 그대로 사용
        filteredCompanies() {
            return this.company_info.companies;
        },

        paginatedCompanies() {
            return this.filteredCompanies;
        },

        totalCompanies() {
            return this.company_info.total_cnt;
        },

        favoriteCompanies() {
            return this.company_info.favorite_cnt || 0;
        },

        recentUpdates() {
            return Math.floor(this.totalCompanies * 0.1);
        },

        displayedPages() {
            const currentGroup = Math.floor((this.currentPage - 1) / 10);
            const startPage = currentGroup * 10 + 1;
            const endPage = Math.min(startPage + 9, this.totalPages);

            const pages = [];
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            return pages;
        },

        formattedLastUpdateDate() {
            if (!this.lastUpdateDate) return '갱신 이력 없음';

            const date = new Date(this.lastUpdateDate);
            return date.toLocaleString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });
        }
    },

    methods: {
        async viewBusinessLicense(companyId) {
            try {
                const imageUrl = await this.getBusinessLicense(companyId);

                if (imageUrl) {
                    await this.$swal.fire({
                        title: '사업자 등록증',
                        html: `<img src="${imageUrl}" style="max-width: 100%; max-height: 600px; border-radius: 8px;" alt="사업자 등록증">`,
                        width: '800px',
                        showCloseButton: true,
                        showConfirmButton: false
                    });
                } else {
                    this.$swal.fire({
                        title: '등록증 없음',
                        text: '등록된 사업자 등록증이 없습니다.',
                        icon: 'info',
                        confirmButtonText: '확인'
                    });
                }
            } catch (error) {
                this.$swal.fire({
                    title: '오류',
                    text: '사업자 등록증을 불러오는데 실패했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        // 사업자 등록증 조회
        async getBusinessLicense(companyId) {
            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getBusinessLicense/${companyId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    }
                );

                if (response.data.success && response.data.data.exists) {
                    return response.data.data.url;
                }
                return null;
            } catch (error) {
                this.$swal.fire({
                    title: '오류',
                    text: '사업자 등록증을 불러오는데 실패했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
                return null;
            }
        },

        // 사업자 등록증 삭제
        async deleteBusinessLicense(companyId) {
            try {
                const { isConfirmed } = await this.$swal.fire({
                    title: '사업자 등록증 삭제',
                    text: '사업자 등록증을 삭제하시겠습니까?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소'
                });

                if (!isConfirmed) return;

                const response = await this.$axios.delete(
                    `${this.$apiUrls.hanqmedi}/deleteBusinessLicense/${this.$globalState.company_key}/${companyId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    }
                );

                if (response.data.success) {
                    this.$swal.fire({
                        title: '삭제 완료',
                        text: '사업자 등록증이 삭제되었습니다.',
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            } catch (error) {
                console.error('사업자 등록증 삭제 실패:', error);
                this.$swal.fire({
                    title: '삭제 실패',
                    text: '사업자 등록증 삭제 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        // ===========================================================================
        // 거래처 유형(구분) 관리
        // ===========================================================================
        async loadCompanyTypes() {
            try {
                if (!this.$globalState.company_key) {
                    return;
                }

                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getCompanyTypes`,
                    {
                        params: { companykey: this.$globalState.company_key },
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    }
                );

                if (response.data.success) {
                    this.customCompanyTypes = response.data.data || [];

                    // companyTypes 객체 업데이트
                    this.companyTypes = { '00': '전체' };
                    this.customCompanyTypes.forEach(type => {
                        this.companyTypes[type.type_code] = type.type_name;
                    });
                }
            } catch (error) {
                console.error('거래처 유형 로드 실패:', error);
                // 기본값 유지
            }
        },

        openCompanyTypeModal() {
            if (!this.checkAdminPermission()) {
                this.$swal.fire({
                    title: '권한 없음',
                    text: '구분 관리는 관리자만 가능합니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }
            this.showCompanyTypeModal = true;
        },

        // ✅ 모달에서 구분이 업데이트되었을 때 호출
        async handleCompanyTypeUpdated() {
            await this.loadCompanyTypes();
            // 현재 선택된 구분이 삭제된 경우 전체로 변경
            if (!this.companyTypes[this.searchType]) {
                this.searchType = '00';
                this.getCompany();
            }
        },

        checkAdminPermission() {
            try {
                const userInfo = localStorage.getItem('userInfo');
                if (userInfo) {
                    const parsedInfo = JSON.parse(userInfo);
                    return parsedInfo.role === 'admin';
                }
                return false;
            } catch (error) {
                return false;
            }
        },

        // ===========================================================================
        // 거래처 등록 관련
        // ===========================================================================
        openRegistrationModal() {
            this.showRegistrationModal = true;
            this.showRegistrationSwal();
        },

        openBulkRegistrationModal() {
            this.showBulkRegistrationModal = true;
            this.showBulkRegistrationSwal();
        },

        async showRegistrationSwal() {
            try {
                const { value: formValues } = await this.$swal.fire({
                    title: '거래처 등록',
                    html: this.getRegistrationFormHTML(),
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: '등록',
                    cancelButtonText: '취소',
                    allowOutsideClick: false,
                    width: '600px',
                    preConfirm: () => {
                        return this.validateRegistrationForm();
                    },
                    didOpen: () => {
                        // DOM이 완전히 렌더링된 후 초기화
                        this.$nextTick(() => {
                            this.initializeAddressSearch();
                            this.initializeBusinessLicenseUpload(); // ✅ 추가
                        });
                    }
                });

                if (formValues) {
                    await this.registerClient(formValues);
                }
            } catch (error) {
                console.error('거래처 등록 모달 오류:', error);
                this.$swal.fire({
                    title: '오류 발생',
                    text: '거래처 등록 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        validateRegistrationForm() {
            // DOM 요소 존재 여부 확인
            const companyIdElement = document.getElementById('company_id');
            const companyNameElement = document.getElementById('company_name');
            const addressElement = document.getElementById('address');
            const companyTypeElement = document.getElementById('company_type');

            if (!companyIdElement || !companyNameElement || !addressElement || !companyTypeElement) {
                this.$swal.showValidationMessage('폼 요소를 찾을 수 없습니다. 페이지를 새로고침해주세요.');
                return false;
            }

            const company_id = companyIdElement.value.trim();
            const company_name = companyNameElement.value;
            const address = addressElement.value.trim();
            const company_type = companyTypeElement.value.trim();

            if (!company_id) {
                this.$swal.showValidationMessage('기관기호를 입력해주세요.');
                return false;
            }

            if (!company_name) {
                this.$swal.showValidationMessage('거래처명을 입력해주세요.');
                return false;
            }

            if (!address) {
                this.$swal.showValidationMessage('주소를 입력해주세요.');
                return false;
            }

            if (!company_type) {
                this.$swal.showValidationMessage('거래처 유형을 선택해주세요.');
                return false;
            }

            return {
                company_id,
                company_name,
                address,
                company_type
            };
        },

        // *----------- 거래처 등록 -------------*
        async registerClient(formValues) {
            let companyData;

            try {
                // DOM 요소 존재 여부 확인
                const companyIdElement = document.getElementById('company_id');
                const companyNameElement = document.getElementById('company_name');
                const addressElement = document.getElementById('address');
                const addressDetailElement = document.getElementById('address_detail');
                const companyNumberElement = document.getElementById('company_number');
                const companyCeoNameElement = document.getElementById('company_ceo_name');
                const telElement = document.getElementById('tel');
                const faxElement = document.getElementById('fax');
                const emailElement = document.getElementById('email');
                const industryElement = document.getElementById('industry');
                const companyTypeElement = document.getElementById('company_type');
                const businessLicenseElement = document.getElementById('business_license');

                if (!companyIdElement || !companyNameElement || !addressElement || !companyTypeElement) {
                    throw new Error('필수 폼 요소를 찾을 수 없습니다.');
                }

                // 주소와 상세주소 합치기
                const address = addressElement.value.trim() || '';
                const addressDetail = addressDetailElement ? addressDetailElement.value.trim() || '' : '';
                const fullAddress = addressDetail ? `${address} ${addressDetail}` : address;

                // ✅ HTTP 통신으로 FormData 사용
                const formData = new FormData();
                formData.append('company_id', companyIdElement.value.trim());
                formData.append('company_name', companyNameElement.value.trim() || '');
                formData.append('company_number', companyNumberElement ? companyNumberElement.value.trim() || '' : '');
                formData.append('company_ceo_name', companyCeoNameElement ? companyCeoNameElement.value.trim() || '' : '');
                formData.append('address', fullAddress);
                formData.append('industry', industryElement ? industryElement.value.trim() || '' : '');
                formData.append('tel', telElement ? telElement.value.trim() || '' : '');
                formData.append('fax', faxElement ? faxElement.value.trim() || '' : '');
                formData.append('email', emailElement ? emailElement.value.trim() || '' : '');
                formData.append('company_type', companyTypeElement.value.trim() || '');
                formData.append('companykey', this.$globalState.company_key);

                // 사업자 등록증 파일 추가
                if (businessLicenseElement && businessLicenseElement.files[0]) {
                    formData.append('business_license', businessLicenseElement.files[0]);
                } else {
                    console.warn('사업자 등록증 파일이 선택되지 않았습니다.');
                }

                // 백엔드 API 호출
                const response = await this.$axios({
                    method: 'post',
                    url: `${this.$apiUrls.hanqmedi}/registerClient`,
                    data: formData,
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.data && response.data.success) {
                    await this.$swal.fire({
                        title: '등록 완료',
                        text: `"${companyNameElement.value.trim()}"거래처가 성공적으로 등록되었습니다.`,
                        icon: 'success',
                        confirmButtonText: '확인'
                    });

                    this.showRegistrationModal = false;
                    await this.getCompany();
                } else {
                    throw new Error(response.data?.message || '거래처 등록에 실패했습니다.');
                }

            } catch (error) {
                console.error('거래처 등록 오류:', {
                    error: {
                        message: error.message,
                        response: error.response?.data,
                        status: error.response?.status
                    },
                    timestamp: new Date().toISOString(),
                    companyData: companyData || '데이터 수집 실패'
                });

                let errorMessage = '거래처 등록 중 오류가 발생했습니다.';
                let errorTitle = '등록 실패';

                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                if (error.response?.status === 400) {
                    errorTitle = '입력 오류';
                } else if (error.response?.status === 401) {
                    errorTitle = '인증 오류';
                } else if (error.response?.status === 500) {
                    errorTitle = '서버 오류';
                }

                this.$swal.fire({
                    title: errorTitle,
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: '확인',
                    showCloseButton: true
                });
            }
        },

        // *----------- 거래처 등록 폼 -------------*
        getRegistrationFormHTML() {
            return `
                <div class="registration-form">
                    <style>
                        .registration-form {
                            max-height: 600px;
                            overflow-y: auto;
                            padding: 10px;
                        }
                        
                        .form-row {
                            display: flex;
                            gap: 16px;
                            margin-bottom: 20px;
                        }
                        
                        .form-group {
                            flex: 1;
                            display: flex;
                            flex-direction: column;
                        }
                        
                        .form-group.full-width {
                            flex: none;
                            width: 100%;
                        }
                        
                        .form-group label {
                            font-size: 15px;
                            font-weight: 600;
                            color: #374151;
                            margin-bottom: 6px;
                            display: block;
                        }
                        
                        .form-group input,
                        .form-group select {
                            padding: 12px 16px;
                            border: 2px solid #e5e7eb;
                            border-radius: 12px;
                            font-size: 14px;
                            transition: all 0.2s ease;
                            background: #ffffff;
                            color: #1f2937;
                        }
                        
                        .form-group input:focus,
                        .form-group select:focus {
                            outline: none;
                            border-color: #0f4c5c;
                        }
                        
                        .form-group input::placeholder {
                            color: #9ca3af;
                        }
                        
                        .address-input-group {
                            display: flex;
                            gap: 8px;
                            align-items: center;
                        }
                        
                        .address-input-group input {
                            flex: 1;
                        }
                        
                        .address-search-btn {
                            padding: 12px 20px;
                            background: linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%);
                            color: white;
                            border: none;
                            border-radius: 12px;
                            font-size: 13px;
                            font-weight: 600;
                            cursor: pointer;
                            transition: all 0.3s ease;
                            white-space: nowrap;
                            box-shadow: 0 4px 15px rgba(15, 76, 92, 0.2);
                            position: relative;
                            overflow: hidden;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 8px;
                            min-width: 120px;
                        }
                        
                        .address-search-btn::before {
                            content: '';
                            position: absolute;
                            top: 0;
                            left: -100%;
                            width: 100%;
                            height: 100%;
                            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                            transition: left 0.5s;
                        }
                        
                        .address-search-btn:hover {
                            background: linear-gradient(135deg, #1a6b7a 0%, #0f4c5c 100%);
                            transform: translateY(-2px);
                            box-shadow: 0 6px 20px rgba(15, 76, 92, 0.3);
                        }
                        
                        .address-search-btn:hover::before {
                            left: 100%;
                        }
                        
                        .address-search-btn:active {
                            transform: translateY(0);
                            box-shadow: 0 2px 10px rgba(15, 76, 92, 0.2);
                        }
                        
                        .address-search-btn:focus {
                            outline: none;
                            box-shadow: 0 0 0 3px rgba(15, 76, 92, 0.1), 0 4px 15px rgba(15, 76, 92, 0.2);
                        }
                        
                        .address-search-btn .search-icon {
                            width: 16px;
                            height: 16px;
                            fill: currentColor;
                        }
                        
                        .form-group input[readonly] {
                            background: #f9fafb;
                            color: #6b7280;
                            cursor: pointer;
                        }
                        
                        .form-group input[readonly]:hover {
                            background: #f3f4f6;
                        }
                        
                        /* 스크롤바 스타일링 */
                        .registration-form::-webkit-scrollbar {
                            width: 6px;
                        }
                        
                        .registration-form::-webkit-scrollbar-track {
                            background: #f1f5f9;
                            border-radius: 3px;
                        }
                        
                        .registration-form::-webkit-scrollbar-thumb {
                            background: #cbd5e1;
                            border-radius: 3px;
                        }
                        
                        .registration-form::-webkit-scrollbar-thumb:hover {
                            background: #94a3b8;
                        }

                        /* 스크롤바 스타일링 */
                        .registration-form::-webkit-scrollbar {
                            width: 6px;
                        }
                        
                        .registration-form::-webkit-scrollbar-track {
                            background: #f1f5f9;
                            border-radius: 3px;
                        }
                        
                        .registration-form::-webkit-scrollbar-thumb {
                            background: #cbd5e1;
                            border-radius: 3px;
                        }
                        
                        .registration-form::-webkit-scrollbar-thumb:hover {
                            background: #94a3b8;
                        }

                        .business-license-row {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 16px;
                            width: 100%;
                        }

                        .business-license-row label {
                            margin-bottom: 0;
                            font-weight: 600;
                            color: #1e293b;
                            white-space: nowrap;
                        }

                        .file-upload-container {
                            display: flex;
                            align-items: center;
                            gap: 12px;
                            flex: 1;
                            justify-content: flex-start;
                        }

                        .file-upload-btn {
                            padding: 8px 16px;
                            background: linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%);
                            color: white;
                            border: none;
                            border-radius: 8px;
                            font-size: 13px;
                            cursor: pointer;
                            box-shadow: 0 2px 8px rgba(15, 76, 92, 0.2);
                            width: fit-content;
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            white-space: nowrap;
                        }

                        .file-upload-btn:hover {
                            background: linear-gradient(135deg, #1a6b7a 0%, #0f4c5c 100%);
                            transform: translateY(-2px);
                            box-shadow: 0 4px 12px rgba(15, 76, 92, 0.3);
                        }

                        .file-upload-btn:active {
                            transform: translateY(0);
                            box-shadow: 0 2px 6px rgba(15, 76, 92, 0.25);
                        }

                        .file-name-display {
                            font-size: 13px;
                            color: #64748b;
                            font-weight: 500;
                            white-space: nowrap;
                            max-width: 200px;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }
                    </style>

                    <div class="form-row">
                        <div class="form-group full-width">
                            <div class="business-license-row">
                                <div class="file-upload-container">
                                    <input type="file" id="business_license" accept="image/jpeg,image/jpg,image/png" 
                                        style="display: none;">
                                    <button type="button" id="businessLicenseBtn" class="file-upload-btn" 
                                        onclick="document.getElementById('business_license').click()">
                                        📄 사업자 등록증 선택
                                    </button>
                                    <span id="businessLicenseFileName" class="file-name-display"></span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="form-row">
                        <div class="form-group">
                            <label for="company_name">거래처명 <span style="color: red;">*</span></label>
                            <input type="text" id="company_name" placeholder="거래처명을 입력하세요">
                        </div>
                        <div class="form-group">
                            <label for="company_id">기관기호</label>
                            <input type="text" id="company_id" placeholder="기관기호를 입력하세요">
                        </div>
                    </div>
                    
                    <div class="form-row">
                        <div class="form-group">
                            <label for="company_number">사업자번호 <span style="color: red;">*</span></label>
                            <input type="text" id="company_number" placeholder="사업자번호를 입력하세요 (- 제외)">
                        </div>
                        <div class="form-group">
                            <label for="company_ceo_name">대표자명 <span style="color: red;">*</span></label>
                            <input type="text" id="company_ceo_name" placeholder="대표자명을 입력하세요">
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-group">
                            <label for="tel">전화번호 <span style="color: red;">*</span></label>
                            <input type="text" id="tel" placeholder="전화번호를 입력하세요">
                        </div>
                        <div class="form-group">
                            <label for="fax">팩스 <span style="color: red;">*</span></label>
                            <input type="text" id="fax" placeholder="팩스번호를 입력하세요">
                        </div>
                    </div>
                    
                    <div class="form-row">
                        <div class="form-group">
                            <label for="email">이메일 <span style="color: red;">*</span></label>
                            <input type="text" id="email" placeholder="이메일을 입력하세요">
                        </div>
                        <div class="form-group">
                            <label for="industry">업태 <span style="color: red;">*</span></label>
                            <input type="text" id="industry" placeholder="업태를 입력하세요">
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="company_type">거래처 유형 <span style="color: red;">*</span></label>
                        <select id="company_type" style="margin-bottom: 10px;">
                            <option value=""">거래처 유형을 선택하세요</option>
                            ${this.customCompanyTypes.map(type =>
                `<option value="${type.type_code}">${type.type_name}</option>`
            ).join('')}
                        </select>
                    </div>
                    
                    <div class="form-row">
                        <div class="form-group full-width">
                            <label for="address">주소 <span style="color: red;">*</span></label>
                            <div class="address-input-group">
                                <input type="text" id="address" placeholder="주소를 입력하세요" readonly>
                                <button type="button" id="addressSearchBtn" class="address-search-btn">
                                    주소 검색
                                </button>
                            </div>
                            <input type="text" id="address_detail" placeholder="상세주소를 입력하세요" style="margin-top: 8px;">
                        </div>
                    </div>
                </div>
            `;
        },

        getBulkRegistrationHTML() {
            return `
                <div class="bulk-registration-modal">
                    <style>
                        .bulk-registration-modal {
                            text-align: center;
                            padding: 20px 0;
                        }
                        
                        .bulk-description {
                            font-size: 14px;
                            color: #6b7280;
                            margin-bottom: 32px;
                            line-height: 1.5;
                        }
                        
                        .bulk-actions {
                            display: flex;
                            flex-direction: column;
                            gap: 16px;
                            align-items: center;
                        }
                        
                        .bulk-btn {
                            padding: 16px 32px;
                            border: none;
                            border-radius: 16px;
                            font-size: 15px;
                            font-weight: 600;
                            cursor: pointer;
                            transition: all 0.3s ease;
                            display: flex;
                            align-items: center;
                            gap: 12px;
                            min-width: 200px;
                            justify-content: center;
                        }
                        
                        .bulk-btn.primary {
                            background: linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%);
                            color: white;
                            box-shadow: 0 4px 15px rgba(15, 76, 92, 0.2);
                        }
                        
                        .bulk-btn.primary:hover {
                            background: linear-gradient(135deg, #1a6b7a 0%, #0f4c5c 100%);
                            transform: translateY(-2px);
                            box-shadow: 0 6px 20px rgba(15, 76, 92, 0.3);
                        }
                        
                        .bulk-btn.secondary {
                            background: #f8fafc;
                            color: #374151;
                            border: 2px solid #e2e8f0;
                        }
                        
                        .bulk-btn.secondary:hover {
                            background: #f1f5f9;
                            border-color: #cbd5e1;
                            transform: translateY(-1px);
                        }
                        
                        .bulk-btn:active {
                            transform: translateY(0);
                        }
                        
                        .bulk-info {
                            margin-top: 24px;
                            padding: 16px;
                            background: #f0f9ff;
                            border-radius: 12px;
                        }
                        
                        .bulk-info h4 {
                            margin: 0 0 8px 0;
                            color: #0369a1;
                            font-size: 14px;
                            font-weight: 600;
                        }
                        
                        .bulk-info p {
                            margin: 0;
                            color: #0c4a6e;
                            font-size: 13px;
                            line-height: 1.4;
                        }
                    </style>
                    
                    <p class="bulk-description">
                        엑셀 템플릿을 다운로드하여 거래처 정보를 입력한 후<br>
                        업로드하여 여러 거래처를 한 번에 등록할 수 있습니다.
                    </p>
                    
                    <div class="bulk-actions">
                        <button type="button" class="bulk-btn secondary" id="downloadTemplateBtn">
                            📥 템플릿 다운로드
                        </button>
                        <button type="button" class="bulk-btn primary" id="uploadExcelBtn">
                            📤 엑셀 파일 업로드
                        </button>
                    </div>
                    
                    <div class="bulk-info">
                        <h4>📋 템플릿 사용 방법</h4>
                        <p>
                            1. 템플릿을 다운로드하여 거래처 정보를 입력하세요<br>
                            2. 엑셀 파일 내 모든 항목을 입력하세요<br>
                            3. 엑셀 파일을 업로드하여 일괄등록을 진행하세요
                        </p>
                    </div>
                </div>
            `;
        },

        async showBulkRegistrationSwal() {
            try {
                const { value: formValues } = await this.$swal.fire({
                    title: '거래처 일괄등록',
                    html: this.getBulkRegistrationHTML(),
                    focusConfirm: false,
                    showCancelButton: false,
                    confirmButtonText: '닫기',
                    width: '500px',
                    didOpen: () => {
                        this.initializeBulkRegistrationEvents();
                    }
                });
            } catch (error) {
                console.error('거래처 일괄등록 모달 오류:', error);
            }
        },

        initializeBulkRegistrationEvents() {
            // 템플릿 다운로드 버튼
            const downloadBtn = document.getElementById('downloadTemplateBtn');
            if (downloadBtn) {
                downloadBtn.addEventListener('click', this.downloadTemplate);
            }

            // 엑셀 업로드 버튼
            const uploadBtn = document.getElementById('uploadExcelBtn');
            if (uploadBtn) {
                uploadBtn.addEventListener('click', this.uploadExcelFile);
            }
        },

        async downloadTemplate() {
            try {
                // 로딩 표시
                const loading = this.$swal.fire({
                    title: '템플릿 다운로드 중...',
                    text: '잠시만 기다려주세요.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                let response;

                // 일렉트론 환경에서 안정적인 다운로드 처리
                if (this.$env.isElectron) {
                    try {
                        // 방법 1: IPC를 통한 다운로드 (권장)
                        if (window.electronAPI && window.electronAPI.downloadFile) {
                            const result = await window.electronAPI.downloadFile(
                                `${this.$apiUrls.hanqmedi}/downloadCompanyExcelTemplate`,
                                '거래처 일괄등록 템플릿.xlsx'
                            );

                            if (result.success) {
                                loading.close();
                                this.$swal.fire({
                                    title: '다운로드 완료',
                                    text: '엑셀 템플릿이 다운로드 폴더에 저장되었습니다.',
                                    icon: 'success',
                                    timer: 3000,
                                    showConfirmButton: false
                                });
                                return;
                            } else {
                                throw new Error(result.error || '다운로드 실패');
                            }
                        }
                    } catch (ipcError) {
                        console.warn('IPC 다운로드 실패, 직접 axios 사용:', ipcError);
                    }

                    // 방법 2: 직접 axios 사용 (fallback)
                    const directAxios = (await import('axios')).default;

                    response = await directAxios.get(
                        `${this.$apiUrls.hanqmedi}/downloadCompanyExcelTemplate`,
                        {
                            responseType: 'blob',
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                            },
                            timeout: 60000, // 60초로 증가
                            maxContentLength: 50 * 1024 * 1024, // 50MB 제한
                            validateStatus: (status) => status < 500 // 500 에러는 throw
                        }
                    );

                } else {
                    // 웹 환경
                    console.log('웹 환경에서 다운로드 처리');

                    const directAxios = (await import('axios')).default;

                    response = await directAxios.get(
                        `${this.$apiUrls.hanqmedi}/downloadCompanyExcelTemplate`,
                        {
                            responseType: 'blob',
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            timeout: 30000
                        }
                    );
                }

                loading.close();

                // 응답 데이터 검증
                if (!response.data || response.data.size === 0) {
                    throw new Error('다운로드된 파일이 비어있습니다.');
                }

                // 파일 다운로드 처리
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                // 파일명 설정 (한글 파일명 처리)
                const fileName = '거래처 일괄등록 템플릿.xlsx';

                // 일렉트론 환경에서 다운로드 처리
                if (this.$env.isElectron) {
                    try {
                        // 일렉트론의 다운로드 API 사용
                        if (window.electronAPI && window.electronAPI.saveFile) {
                            const base64Data = await this.blobToBase64(blob);
                            const result = await window.electronAPI.saveFile(fileName, base64Data);

                            if (result.success) {
                                this.$swal.fire({
                                    title: '다운로드 완료',
                                    text: `파일이 저장되었습니다: ${result.filePath}`,
                                    icon: 'success',
                                    timer: 3000,
                                    showConfirmButton: false
                                });
                                return;
                            }
                        }
                    } catch (saveError) {
                        console.warn('일렉트론 저장 실패, 브라우저 다운로드 사용:', saveError);
                    }
                }

                // 브라우저 다운로드 (fallback)
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                link.style.display = 'none';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // 메모리 정리
                setTimeout(() => {
                    window.URL.revokeObjectURL(url);
                }, 1000);

                this.$swal.fire({
                    title: '다운로드 완료',
                    text: '엑셀 템플릿이 성공적으로 다운로드되었습니다.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

            } catch (error) {
                console.error('템플릿 다운로드 오류:', error);

                // 더 자세한 에러 정보 로깅
                if (error.response) {
                    console.error('서버 응답 상세:', {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        headers: error.response.headers,
                        data: error.response.data
                    });
                }

                let errorMessage = '엑셀 템플릿 다운로드 중 오류가 발생했습니다.';

                if (error.response) {
                    switch (error.response.status) {
                        case 401:
                            errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
                            break;
                        case 403:
                            errorMessage = '템플릿 다운로드 권한이 없습니다.';
                            break;
                        case 404:
                            errorMessage = '템플릿 파일을 찾을 수 없습니다.';
                            break;
                        case 500:
                            errorMessage = '서버에서 템플릿 생성 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
                            break;
                        default:
                            errorMessage = `서버 오류 (${error.response.status}): ${error.response.statusText}`;
                    }
                } else if (error.code === 'ECONNABORTED') {
                    errorMessage = '다운로드 시간이 초과되었습니다. 네트워크 상태를 확인해주세요.';
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    title: '다운로드 실패',
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        uploadExcelFile() {
            // 파일 업로드 input 생성
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.csv,.xlsx,.xls';
            input.style.display = 'none';

            input.onchange = (event) => {
                const file = event.target.files[0];
                if (file) {
                    this.processExcelFile(file);
                }
            };

            document.body.appendChild(input);
            input.click();
            document.body.removeChild(input);
        },

        async processExcelFile(file) {
            try {
                // 파일 확장자 체크
                const fileExt = file.name.split('.').pop().toLowerCase();
                if (fileExt !== 'xlsx') {
                    this.$swal.fire('오류', 'Excel 파일(.xlsx)만 업로드 가능합니다.', 'error');
                    return;
                }

                // companykey 확인
                if (!this.$globalState.company_key) {
                    this.$swal.fire('오류', '회사 정보가 없습니다. 다시 로그인해주세요.', 'error');
                    return;
                }

                // 로딩 표시
                const loading = this.$swal.fire({
                    title: '거래처 엑셀 업로드 중...',
                    text: '잠시만 기다려주세요.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                let response;

                // 일렉트론 환경에서 IPC 파일 업로드 사용
                if (this.$env.isElectron) {

                    // 파일을 base64로 변환
                    const base64Data = await this.fileToBase64(file);

                    // 데이터 구조 수정 - fileData를 직접 전달
                    const formData = {
                        fileName: file.name,
                        fileData: base64Data,
                        companykey: this.$globalState.company_key
                    };

                    // 토큰을 config로 전달
                    const config = {
                        token: localStorage.getItem('token')
                    };

                    // IPC를 통한 파일 업로드 - company 타입 사용
                    const uploadResult = await window.electronAPI.uploadFile('company', formData, config);

                    if (uploadResult.success) {
                        response = { data: uploadResult.data };
                    } else {
                        throw new Error(uploadResult.error || '파일 업로드 실패');
                    }

                } else {
                    // 웹 환경에서 FormData 사용
                    const formData = new FormData();
                    formData.append('company', file);
                    formData.append('companykey', this.$globalState.company_key);

                    const directAxios = (await import('axios')).default;

                    response = await directAxios.post(
                        `${this.$apiUrls.hanqmedi}/uploadCompanyExcel`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            timeout: 300000,
                            maxContentLength: 100 * 1024 * 1024
                        }
                    );
                }

                loading.close();

                // 상세 결과 정보 구성
                const result = response.data.data;
                const totalRows = result.rowsProcessed + result.rowsFailed;

                let resultHtml = `
                    <div style="text-align: center; margin-bottom: 15px;">
                        <h4 style="color: #28a745; margin-bottom: 10px;">📊 거래처 등록 결과</h4>
                        <p><strong>총 처리 건수:</strong> ${totalRows}건</p>
                        <p><strong>성공:</strong> <span style="color: #28a745;">${result.rowsProcessed}건</span></p>
                        <p><strong>실패:</strong> <span style="color: #dc3545;">${result.rowsFailed}건</span></p>
                    </div>
                `;

                // 실패한 행이 있는 경우 상세 정보 표시
                if (result.rowsFailed > 0 && result.failedRows && result.failedRows.length > 0) {
                    resultHtml += `
                        <div style="text-align: center; margin-top: 15px;">
                            <h5 style="color: #dc3545; margin-bottom: 10px;">❌ 실패한 행 상세정보</h5>
                            <div style="max-height: 200px; overflow-y: auto; background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
                    `;

                    result.failedRows.forEach(({ rowIndex, error, data }) => {
                        resultHtml += `
                            <div style="margin-bottom: 8px; padding: 5px; border-left: 3px solid #dc3545; background: white;">
                                <strong>${rowIndex}번 행:</strong> ${error}<br>
                                <small style="color: #6c757d;">데이터: ${JSON.stringify(data)}</small>
                            </div>
                        `;
                    });

                    resultHtml += `
                            </div>
                        </div>
                    `;
                }

                this.$swal.fire({
                    title: '거래처 등록 완료',
                    html: resultHtml,
                    icon: 'success',
                    width: '600px',
                    confirmButtonText: '확인',
                    showCloseButton: true
                });

                // 목록 새로고침
                this.getCompany();

            } catch (error) {
                console.error('거래처 엑셀 업로드 오류:', error);

                // 더 자세한 에러 정보 로깅
                if (error.response) {
                    console.error('서버 응답 상세:', {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        data: error.response.data,
                        headers: error.response.headers
                    });
                }

                let errorMessage = '거래처 엑셀 업로드 중 오류가 발생했습니다.';

                if (error.response) {
                    switch (error.response.status) {
                        case 400:
                            errorMessage = error.response.data.message || '잘못된 요청입니다.';
                            break;
                        case 401:
                            errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
                            break;
                        case 413:
                            errorMessage = '파일 크기가 너무 큽니다.';
                            break;
                        case 500:
                            errorMessage = error.response.data.message || '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
                            break;
                        default:
                            errorMessage = `서버 오류 (${error.response.status}): ${error.response.data.message || error.response.statusText}`;
                    }
                } else if (error.code === 'ECONNABORTED') {
                    errorMessage = '업로드 시간이 초과되었습니다. 네트워크 상태를 확인해주세요.';
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    title: '업로드 실패',
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        // 파일을 base64로 변환하는 메서드 추가
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },

        // ===========================================================================
        // 상태 저장/복원
        saveState() {
            const state = {
                searchQuery: this.searchQuery,
                searchSubmitted: this.searchSubmitted,
                searchType: this.searchType,
                searchArea: this.searchArea,
                favoriteFilter: this.favoriteFilter,
                currentPage: this.currentPage,
                itemsPerPage: this.itemsPerPage,
                lastVisit: new Date().getTime()
            };

            localStorage.setItem('companyManageState', JSON.stringify(state));
        },

        restoreState() {
            const isRefresh = sessionStorage.getItem('companyManageVisited') === 'true';

            if (isRefresh) {
                console.log('새로고침 감지: 상태 초기화');
                localStorage.removeItem('companyManageState');
                sessionStorage.removeItem('companyManageVisited');
                return;
            }

            const savedState = localStorage.getItem('companyManageState');
            if (!savedState) return;

            try {
                const state = JSON.parse(savedState);
                this.searchQuery = state.searchQuery || '';
                this.searchSubmitted = state.searchSubmitted || '';
                this.searchType = state.searchType || '00';
                this.searchArea = state.searchArea || '000';
                this.favoriteFilter = state.favoriteFilter || 'all';
                this.currentPage = state.currentPage || 1;
                this.itemsPerPage = state.itemsPerPage || 20;
            } catch (error) {
                console.error('상태 복원 중 오류 발생:', error);
                localStorage.removeItem('companyManageState');
            }
        },

        // 검색 및 필터링
        searchHandler() {
            this.currentPage = 1;
            this.getCompany();
            this.saveState();
        },

        // 필터 메서드들
        filterByType(typeCode) {
            this.searchType = typeCode;
            this.currentPage = 1;
            this.getCompany();
            this.saveState();
        },

        filterByArea(areaCode) {
            this.searchArea = areaCode;
            this.currentPage = 1;
            this.getCompany();
            this.saveState();
        },

        // 개별 필터 초기화
        clearFilter(filterType) {
            switch (filterType) {
                case 'type':
                    this.searchType = '00';
                    break;
                case 'area':
                    this.searchArea = '000';
                    break;
                case 'favorite':
                    this.favoriteFilter = 'all';
                    break;
            }
            this.currentPage = 1;
            this.getCompany();
            this.saveState();
        },

        // 모든 필터 초기화
        clearAllFilters() {
            this.searchType = '00';
            this.searchArea = '000';
            this.favoriteFilter = 'all';
            this.currentPage = 1;
            this.getCompany();
            this.saveState();
        },

        // 거래처 삭제
        async deleteCompany(companyId) {
            try {
                const company = this.company_info.companies.find(c => c.company_id === companyId);
                if (!company) {
                    this.$swal.fire('오류', '삭제할 거래처를 찾을 수 없습니다.', 'error');
                    return;
                }

                // 삭제 확인
                const { isConfirmed } = await this.$swal.fire({
                    title: '거래처 삭제 확인',
                    text: `"${company.company_name}" 거래처를 정말로 삭제하시겠습니까?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#dc2626',
                    reverseButtons: true
                });

                if (!isConfirmed) return;

                // 로딩 표시
                this.$swal.fire({
                    title: '거래처 삭제 중...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                // API 호출
                const response = await this.$axios.delete(
                    `${this.$apiUrls.hanqmedi}/deleteCompany`,
                    {
                        data: {
                            company_id: companyId,
                            company_key: this.$globalState.company_key
                        },
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    }
                );

                if (response.data.success) {
                    // 성공 메시지
                    this.$swal.fire({
                        icon: 'success',
                        title: '삭제 완료',
                        text: response.data.message,
                        timer: 2000,
                        showConfirmButton: false
                    });

                    // 로컬 데이터에서 제거
                    this.company_info.companies = this.company_info.companies.filter(c => c.company_id !== companyId);
                    this.company_info.total_cnt--;

                    // 통계 업데이트
                    if (response.data.data.updated_stats) {
                        this.company_info.total_cnt = response.data.data.updated_stats.total_companies;
                        this.company_info.favorite_cnt = response.data.data.updated_stats.total_favorites;
                    }

                    // 상태 저장
                    this.saveState();

                    console.log('거래처 삭제 완료:', response.data.data);
                } else {
                    throw new Error(response.data.message || '삭제에 실패했습니다.');
                }

            } catch (error) {
                console.error('거래처 삭제 실패:', error);

                let errorMessage = '거래처 삭제 중 오류가 발생했습니다.';
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '삭제 실패',
                    text: errorMessage,
                    confirmButtonText: '확인'
                });
            }
        },

        // 거래처 데이터 조회
        async getCompany() {
            if (this.isLoading) return;
            this.isLoading = true;

            try {
                // company_key 확인 및 설정
                if (!this.$globalState.company_key) {
                    const userInfo = localStorage.getItem('userInfo');
                    if (userInfo) {
                        const parsedInfo = JSON.parse(userInfo);
                        this.$setGlobalState('id', parsedInfo.id);
                        this.$setGlobalState('manager_name', parsedInfo.name);
                        this.$setGlobalState('company_name', parsedInfo.company);
                        this.$setGlobalState('company_key', parsedInfo.company_key);
                    } else {
                        this.$swal.fire('인증 오류', '로그인이 필요합니다.', 'error');
                        this.$router.push('/login');
                        return;
                    }
                }

                // company_key가 여전히 없으면 에러 처리
                if (!this.$globalState.company_key) {
                    this.$swal.fire('인증 오류', '사업소 정보를 찾을 수 없습니다.', 'error');
                    this.$router.push('/login');
                    return;
                }

                this.searchSubmitted = this.searchQuery;

                const response = await this.$axios({
                    method: 'get',
                    url: `${this.$apiUrls.hanqmedi}/getCompany`,
                    params: {
                        search: this.searchSubmitted,
                        type: this.searchType,
                        area: this.searchArea,
                        page: this.currentPage,
                        perPage: this.itemsPerPage,
                        favorite_filter: this.favoriteFilter,
                        include_stats: true,
                        company_key: this.$globalState.company_key
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    timeout: 30000
                });

                if (response.data) {
                    this.company_info.companies = response.data.companies || [];
                    this.company_info.total_cnt = response.data.total || 0;
                    this.company_info.favorite_cnt = response.data.favorite_cnt || 0;

                    this.totalPages = response.data.total_pages || 1;

                    if (response.data.current_page) {
                        this.currentPage = response.data.current_page;
                    }

                    // companykey 확인 및 필터링
                    const currentCompanyKey = this.$globalState.company_key;
                    this.company_info.companies = this.company_info.companies.filter(company => {
                        if (company.companykey && company.companykey !== currentCompanyKey) {
                            console.warn(`잘못된 companykey 데이터 발견:`, company);
                            return false;
                        }
                        return true;
                    });

                    // 선택 상태 초기화
                    this.company_info.companies.forEach(company => {
                        company.selected = false;
                    });

                    // 검색 결과가 없을 때만 알림 표시
                    if (this.company_info.companies.length === 0 && this.hasActiveFilters) {
                        let searchConditions = [];
                        if (this.searchSubmitted) searchConditions.push(`검색어: "${this.searchSubmitted}"`);
                        if (this.favoriteFilter === 'favorites') searchConditions.push('즐겨찾기');
                        if (this.searchType !== '00') searchConditions.push(`타입: ${this.companyTypes[this.searchType]}`);
                        if (this.searchArea !== '000') searchConditions.push(`지역: ${this.companyAreas[this.searchArea]}`);

                        this.$swal.fire({
                            title: '검색 결과 없음',
                            text: `검색 조건에 맞는 거래처가 없습니다.`,
                            icon: 'info',
                            confirmButtonText: '확인'
                        });
                    }

                    // 상태 저장
                    this.saveState();

                    // 조회 완료 로깅
                    console.log(`조회 완료: companykey ${currentCompanyKey}, 총 ${this.company_info.total_cnt}개, 즐겨찾기 ${this.company_info.favorite_cnt}개`);
                }
            } catch (error) {
                console.error('데이터를 가져오는 중 오류 발생:', error);

                // 에러 상세 정보 로깅
                if (error.response) {
                    console.error('에러 응답:', error.response.data);
                    console.error('에러 상태:', error.response.status);
                }

                this.$swal.fire('오류', '데이터를 불러오는데 실패했습니다.', 'error');
                this.company_info.companies = [];
                this.company_info.total_cnt = 0;
                this.company_info.favorite_cnt = 0;
                this.totalPages = 1;
            } finally {
                this.isLoading = false;
            }
        },

        // 마지막 갱신일 조회
        async getLastUpdateDate() {
            try {
                const response = await this.$axios({
                    method: 'get',
                    url: `${this.$apiUrls.hanqmedi}/getLastUpdateDate`
                });

                if (response.status === 200 && response.data.lastUpdateDate) {
                    this.lastUpdateDate = response.data.lastUpdateDate;
                } else {
                    this.lastUpdateDate = null;
                }
            } catch (error) {
                console.error('마지막 갱신일 조회 중 오류 발생:', error);
                this.lastUpdateDate = null;
            }
        },

        // 거래처 갱신
        async updateCompany() {
            const userInfo = JSON.parse(localStorage.getItem('userInfo'));

            if (this.$globalState.company_key !== 32 || userInfo?.role !== 'admin') {
                this.$swal.fire({
                    title: '권한 없음',
                    text: '거래처 갱신은 관리자만 실행할 수 있습니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }

            const result = await this.$swal.fire({
                title: '거래처 갱신',
                text: `${this.companyTypes[this.selectedUpdateType]} 거래처를 갱신하시겠습니까?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: '갱신',
                cancelButtonText: '취소'
            });

            if (!result.isConfirmed) return;

            this.$swal.fire({
                title: '거래처 정보 갱신',
                html: '거래처 정보를 갱신 중입니다. 잠시만 기다려주세요...',
                allowOutsideClick: false,
                didOpen: () => {
                    this.$swal.showLoading();
                }
            });

            try {
                const response = await this.$axios({
                    method: 'get',
                    url: `${this.$apiUrls.hanqmedi}/updateCompany/${this.selectedUpdateType}`,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    params: {
                        companykey: this.$globalState.company_key
                    },
                    timeout: 3600000, // 1시간으로 설정
                    withCredentials: false,
                });

                if (response.status === 200) {
                    this.$swal.fire({
                        title: '갱신 완료',
                        text: '거래처 정보가 성공적으로 갱신되었습니다.',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false
                    });

                    await this.getCompany();
                    await this.getLastUpdateDate();
                    this.saveState();
                } else {
                    throw new Error('서버 응답 오류');
                }
            } catch (error) {
                console.error('거래처 갱신 중 오류 발생:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '거래처 갱신 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        // 즐겨찾기 업데이트
        async updateFavorite(company) {
            try {
                // company_key가 없으면 localStorage에서 다시 가져오기
                if (!this.$globalState.company_key) {
                    const userInfo = localStorage.getItem('userInfo');
                    if (userInfo) {
                        const parsedInfo = JSON.parse(userInfo);
                        this.$setGlobalState('company_key', parsedInfo.company_key);
                    }
                }

                if (!this.$globalState.company_key) {
                    this.$swal.fire('오류', '사업소 정보를 찾을 수 없습니다.', 'error');
                    return;
                }

                const response = await this.$axios({
                    method: 'post',
                    url: `${this.$apiUrls.hanqmedi}/updateFavorite/${company.company_id}`,
                    data: {
                        company_key: this.$globalState.company_key
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data && response.data.success) {
                    const oldFavorite = company.favorite;
                    company.favorite = response.data.data.favorite;

                    // 전체 즐겨찾기 카운트 업데이트
                    if (oldFavorite !== company.favorite) {
                        if (company.favorite) {
                            this.company_info.favorite_cnt += 1;
                        } else {
                            this.company_info.favorite_cnt -= 1;
                        }
                    }

                    this.$swal.fire({
                        icon: 'success',
                        title: '즐겨찾기 업데이트 완료',
                        text: response.data.data.message,
                        showConfirmButton: false,
                        timer: 1500
                    });
                    this.saveState();
                }
            } catch (error) {
                console.error('즐겨찾기 업데이트 중 오류 발생:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '즐겨찾기 업데이트 실패',
                    text: '즐겨찾기 업데이트에 실패했습니다.',
                    showConfirmButton: false,
                    timer: 1500
                });
            }
        },

        // 페이지네이션
        async goToPage(page) {
            if (page < 1 || page > this.totalPages || page === this.currentPage) {
                return;
            }
            this.currentPage = page;
            await this.getCompany();
            this.saveState();
        },

        async goToPageGroup(direction) {
            let newPage;
            const currentGroup = Math.floor((this.currentPage - 1) / 10);

            if (direction === 'prev') {
                newPage = Math.max(1, (currentGroup - 1) * 10 + 1);
            } else {
                newPage = Math.min(this.totalPages, (currentGroup + 1) * 10 + 1);
            }

            if (newPage !== this.currentPage) {
                this.currentPage = newPage;
                await this.getCompany();
                this.saveState();
            }
        },

        changeItemsPerPage() {
            this.currentPage = 1;
            this.getCompany();
            this.saveState();
        },

        initializeAddressSearch() {
            const addressSearchBtn = document.getElementById('addressSearchBtn');
            if (addressSearchBtn) {
                addressSearchBtn.addEventListener('click', () => {
                    this.searchAddress();
                });
            }
        },

        // 사업자 등록증 업로드 초기화
        initializeBusinessLicenseUpload() {
            const businessLicenseInput = document.getElementById('business_license');

            if (businessLicenseInput) {
                businessLicenseInput.addEventListener('change', (event) => {
                    this.handleBusinessLicenseChange(event);
                });
            }
        },

        searchAddress() {
            new window.daum.Postcode({
                oncomplete: (data) => {
                    document.getElementById('address').value = data.address;
                    document.getElementById('address_detail').focus();
                }
            }).open();
        },

        handleBusinessLicenseChange(event) {
            const file = event.target.files[0];
            const fileNameDisplay = document.getElementById('businessLicenseFileName');

            if (!fileNameDisplay) {
                console.error('파일명 표시 요소를 찾을 수 없습니다.');
                return;
            }

            if (file) {
                // 파일 크기 체크 (10MB)
                if (file.size > 10 * 1024 * 1024) {
                    this.$swal.fire('파일 크기 초과', '파일 크기는 10MB 이하여야 합니다.', 'error');
                    event.target.value = '';
                    fileNameDisplay.textContent = '';
                    return;
                }

                // 파일 타입 체크
                if (!file.type.match('image/jpeg|image/jpg|image/png')) {
                    this.$swal.fire('파일 형식 오류', 'JPG, JPEG, PNG 파일만 업로드 가능합니다.', 'error');
                    event.target.value = '';
                    fileNameDisplay.textContent = '';
                    return;
                }

                // 파일명만 표시
                fileNameDisplay.textContent = `선택된 파일: ${file.name}`;
            } else {
                fileNameDisplay.textContent = '';
            }
        },
    },

    async mounted() {
        this.restoreState();

        if (this.searchSubmitted) {
            this.getCompany();
        } else {
            this.getCompany();
        }

        await this.getLastUpdateDate();
        await this.loadCompanyTypes();
        sessionStorage.setItem('companyManageVisited', 'true');
    },

    activated() {
        sessionStorage.setItem('companyManageVisited', 'true');
    },

    deactivated() {
        this.saveState();
    },

    beforeUnmount() {
        this.saveState();
        sessionStorage.removeItem('companyManageVisited');
    },

    watch: {
        searchType() {
            this.saveState();
        },
        searchArea() {
            this.saveState();
        },
        selectedUpdateType() {
            this.saveState();
        },
        favoriteFilter() {
            this.saveState();
        },
    }
}
</script>

<style>
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>